import type {
  GetCurrentMemberFn,
  SearchMembersFn,
  ListTeamMembersForWorkspaceMemberFn,
} from '@/features/iam'
import type { Alert, AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { mustHaveMaxLength, when } from '@/utils/validx-util'
import { required, validationContext } from 'validx'
import { ClientInputEditorViewModel } from '@/features/jobs/TransferJobDialog/ClientInputEditor/ClientInputEditorViewModel'
import { ProInputEditorViewModel } from '@/features/jobs/TransferJobDialog/ProInputEditor/ProInputEditorViewModel'
import { ClientTeamInputEditorViewModel } from '@/features/jobs/TransferJobDialog/ClientTeamInputEditor/ClientTeamInputEditorViewModel'
import { type ProviderStore } from '@/features/jobs/ProviderStore'
import { type RatingsViewModel } from '@/features/jobs/details/ratings/RatingsViewModel'
import { type BulkActionJobErrorDialogViewModel } from '@/features/jobs/list/components/BulkActionJobErrorDialog/BulkActionJobErrorDialogViewModel'
import type { BulkActionError } from '@/features/jobs/list/bulk-actions'

export type TransferType = 'Pool' | 'Pro' | 'Client' | 'Team' | 'None'

/**
 * Reasons an admin can pick for a transfer.
 */
export const transferReasons: string[] = [
  'Pro unresponsive',
  'Pro requested out',
  'Client / partner requested',
  'Lacked expertise / too complex',
  'Client unresponsive',
  'Other',
]

/**
 * Represents the shape of transfer job options.
 */
export interface TransferJobOption {
  displayName: string
  transferType: TransferType
}

/**
 * Default option selection.
 */
const defaultOptions: TransferJobOption[] = [
  {
    displayName: 'Client',
    transferType: 'Client',
  },
]

/**
 * Consultation option selection.
 */
const consultationOptions: TransferJobOption[] = [
  {
    displayName: 'Pool',
    transferType: 'Pool',
  },
  {
    displayName: 'Pro',
    transferType: 'Pro',
  },
]

/**
 * Client and pool options selection.
 */
const poolAndClientOptions: TransferJobOption[] = [
  {
    displayName: 'Client',
    transferType: 'Client',
  },
  {
    displayName: 'Pool',
    transferType: 'Pool',
  },
]

/**
 * Client and team option selection.
 */
const unclaimedOptions: TransferJobOption[] = [
  {
    displayName: 'Client',
    transferType: 'Client',
  },
  {
    displayName: 'Team',
    transferType: 'Team',
  },
]

/**
 * Client and team option selection.
 */
const noProOptions: TransferJobOption[] = [
  {
    displayName: 'Client',
    transferType: 'Client',
  },
  {
    displayName: 'Pool',
    transferType: 'Pool',
  },
  {
    displayName: 'Team',
    transferType: 'Team',
  },
]

/**
 * Client and team option selection.
 */
const noTeamOptions: TransferJobOption[] = [
  {
    displayName: 'Client',
    transferType: 'Client',
  },
  {
    displayName: 'Pool',
    transferType: 'Pool',
  },
  {
    displayName: 'Pro',
    transferType: 'Pro',
  },
]

/**
 * Options for Transfer selection.
 */
const allOptions: TransferJobOption[] = [
  {
    displayName: 'Client',
    transferType: 'Client',
  },
  {
    displayName: 'Pool',
    transferType: 'Pool',
  },
  {
    displayName: 'Pro',
    transferType: 'Pro',
  },
  {
    displayName: 'Team',
    transferType: 'Team',
  },
]

/**
 * When the selected jobs have no transfer option in common.
 */
const noneOptions: TransferJobOption[] = [
  {
    displayName: '',
    transferType: 'None',
  },
]

/**
 * View Model for the Transfer Job dialog.
 */
export class TransferJobDialogViewModel {
  /**
   * Holds the maximum length of each field.
   */
  private readonly _fieldLengths = {
    reason: { max: 100 },
  }

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The jobs to transfer.
   */
  @observable
  accessor jobs: Job[] = []

  /**
   * Validation context for the view model.
   */
  readonly validation = validationContext(this, {
    reasonOption: [required('A reason is required.')],
    otherReason: [
      when(() => this.isOtherReason, required('A reason is required.')),
      mustHaveMaxLength(
        this._fieldLengths.reason.max,
        `The value can't be greater than ${this._fieldLengths.reason.max} characters.`,
      ),
    ],
  })

  /**
   * The transfer type.
   */
  @observable
  accessor transferType: TransferType = 'None'

  /**
   * Registry of detail view models for each possible type of transfer.
   */
  readonly transferViewModels

  /**
   * The selected reason for the transfer.
   */
  @observable
  accessor reasonOption = ''

  /**
   * The reason typed in when "Other" is selected.
   */
  @observable
  accessor otherReason = ''

  constructor(
    private readonly _findMembers: SearchMembersFn,
    private readonly _findTeamMembersByUserPublicId: ListTeamMembersForWorkspaceMemberFn,
    private readonly _jobStore: JobStore,
    private readonly _providerStore: ProviderStore,
    private readonly _newAlert: AlertFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _ratingsVm?: RatingsViewModel,
    private readonly _bulkActionErrorVm?: BulkActionJobErrorDialogViewModel,
  ) {
    this.transferViewModels = {
      Client: new ClientInputEditorViewModel(
        this._findMembers,
        this._getCurrentMember,
      ),
      Pro: new ProInputEditorViewModel(
        this._findMembers,
        this._providerStore,
        this._getCurrentMember,
      ),
      Team: new ClientTeamInputEditorViewModel(
        this._findTeamMembersByUserPublicId,
      ),
      Pool: null,
    } as const
  }

  /**
   * Transfer options depending on permission.
   */
  @computed
  get filteredTransferOptions() {
    return this.jobs.length === 1 ? this._forSingleJob : this._forMultiJobs
  }

  /**
   * The currently selected transfer type.
   */
  @computed
  get option() {
    return allOptions.find((o) => o.transferType === this.transferType)
  }

  /**
   * Whether to offer rating the Pro being transferred from.
   */
  @computed
  get showRateProButton() {
    return !!this._ratingsVm?.canRatePro && this.transferType === 'Pro'
  }

  /**
   * Whether the transfer cannot be started.
   */
  @computed
  get isTransferDisabled() {
    return (
      this.transfer.pending || this.transferType === 'None' || !this._hasReason
    )
  }

  /**
   * Whether the "Other" reason is selected.
   */
  @computed
  get isOtherReason() {
    return this.reasonOption === 'Other'
  }

  /**
   * The reason of the transfer, as sent to the API.
   */
  @computed
  get reason() {
    return this.isOtherReason ? this.otherReason : this.reasonOption
  }

  /**
   * Helper text for reason field by Transfer Type.
   */
  @computed
  get reasonHelpText() {
    switch (this.transferType) {
      case 'Client':
      case 'Pro':
      case 'Team':
        return `Why are you transferring?
        This text is public and can be seen by the Client and Pros.`
      default:
        return `Why are you transferring to the pool? This text is public
      and can be seen by the Client and Pros.`
    }
  }

  /**
   * Shows the dialog for rating the Pro being transferred from.
   */
  @action.bound
  showProRatingDialog() {
    this._ratingsVm?.showRateProDialog()
  }

  /**
   * Shows Transfer Job Dialog.
   */
  @action.bound
  open(jobs: Job[]) {
    if (this.modal.showing) {
      return
    }

    this.jobs = jobs
    Object.values(this.transferViewModels).forEach((vm) => vm?.setJobs(jobs))

    this.modal.show()
  }

  /**
   * Transfers job by Transfer Type. It could be to the Pool, Pro, Client or Team.
   */
  transfer = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    switch (this.transferType) {
      case 'Pool':
        await this._transferJobToPool()
        break
      case 'Client':
        await this._transferJobToClient()
        break
      case 'Pro':
        await this._transferJobToPro()
        break
      case 'Team':
        await this._transferJobToClientTeam()
        break
      default:
        return Promise.reject(new Error('Transfer type not supported.'))
    }

    this.modal.close()
  })

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    const isThisViewModelValid = this.validation.reset().validate().isValid
    const isClientViewModelValid =
      this.transferViewModels.Client.validateClient()
    const isProViewModelValid = this.transferViewModels.Pro.validate()
    const isClientTeamViewModelValid =
      this.transferViewModels.Team.validateClientTeamMember()

    switch (this.transferType) {
      case 'Client':
        return isThisViewModelValid && isClientViewModelValid
      case 'Pro':
        return isThisViewModelValid && isProViewModelValid
      case 'Team':
        return isThisViewModelValid && isClientTeamViewModelValid
      default:
        return isThisViewModelValid
    }
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.jobs = []
    this.reasonOption = ''
    this.otherReason = ''
    this.transferType = 'None'
    this.transfer.reset()

    this.validation.reset()
    Object.values(this.transferViewModels).forEach((vm) => vm?.reset())
  }

  /**
   * Sets the transfer type.
   */
  @action.bound
  setTransferOption(transferOption?: TransferJobOption) {
    if (transferOption) {
      if (transferOption.transferType === 'Pro') {
        this._ratingsVm?.fetchJobRatingStatus().catch(() => undefined)
      }

      this.transferType = transferOption.transferType
    }
  }

  @computed
  private get _hasReason() {
    return this.isOtherReason
      ? this.otherReason.trim().length > 0
      : this.reasonOption !== ''
  }

  @computed
  private get _forSingleJob() {
    const job = this.jobs[0]

    if (!this._canTransferConsultationJobAsAdmin(job)) {
      return noneOptions
    }

    const poolTransferOption = this._canTransferToPoolAsAdmin(job)
    const clientTransferOption = this._canTransferJobAsAdmin(job)
    const proTransferOption = this._canTransferToProAsAdmin(job)
    const clientTeamTransferOption = this._canTransferToClientTeamAsAdmin(job)
    const canSeeAllOptions =
      clientTransferOption &&
      proTransferOption &&
      clientTeamTransferOption &&
      poolTransferOption
    const canSeeAllExceptProOptions =
      !proTransferOption &&
      clientTransferOption &&
      clientTeamTransferOption &&
      poolTransferOption
    const canSeeAllExceptTeamOptions =
      proTransferOption &&
      clientTransferOption &&
      !clientTeamTransferOption &&
      poolTransferOption

    // If we have a consultation only allow transfer to the pool and provider.
    if (job.hasActiveConsultation) {
      return consultationOptions
    }

    // We check these just in case.
    if (job.isClaimedJob && canSeeAllExceptProOptions) {
      return noProOptions
    }

    if (job.isClaimedJob && canSeeAllExceptTeamOptions) {
      return noTeamOptions
    }

    // Display all transfer options.
    if (job.isClaimedJob && canSeeAllOptions) {
      return allOptions
    }

    // Job on hold with champion provider still shows all transfer options.
    if (job.status === 'OnHold' && job.championProvider && canSeeAllOptions) {
      return allOptions
    }

    // In this case, we only show client/team transfer options.
    if (!job.isClaimedJob && clientTeamTransferOption && clientTransferOption) {
      return unclaimedOptions
    }

    if (job.isClaimedJob && poolTransferOption && clientTransferOption) {
      return poolAndClientOptions
    }

    return defaultOptions
  }

  @computed
  private get _forMultiJobs() {
    if (this.jobs.some((j) => !this._canTransferConsultationJobAsAdmin(j))) {
      return noneOptions
    }

    const clientTransferOption = this.jobs.every((j) =>
      this._canTransferJobAsAdmin(j),
    )

    const clientTeamTransferOption = this.jobs.every(
      (p) =>
        this._canTransferToClientTeamAsAdmin(p) &&
        p.jobType?.type !== 'Consultation',
    )

    const poolTransferOption = this.jobs.every(
      (job) =>
        this._canTransferToPoolAsAdmin(job) &&
        job.proChampion &&
        !job.completed &&
        !job.isDraft,
    )

    const proTransferOption = this.jobs.every(
      (job) =>
        this._canTransferToProAsAdmin(job) &&
        job.proChampion &&
        !job.completed &&
        !job.isDraft,
    )

    const conditions: (TransferJobOption & { enabled: boolean })[] = [
      {
        displayName: 'Pool',
        transferType: 'Pool',
        enabled: poolTransferOption,
      },
      {
        displayName: 'Team',
        transferType: 'Team',
        enabled: clientTeamTransferOption,
      },
      {
        displayName: 'Client',
        transferType: 'Client',
        enabled: clientTransferOption,
      },
      {
        displayName: 'Pro',
        transferType: 'Pro',
        enabled: proTransferOption,
      },
    ]

    const results = conditions.filter((c) => c.enabled)

    return results.length > 0 ? results : noneOptions
  }

  private _handleBulkResponse(
    alert: Alert,
    response: BulkActionError[],
    target?: string,
  ) {
    if (response.length > 0) {
      this._bulkActionErrorVm?.showErrors(response, this.jobs)
    }

    // all selected jobs failed the transfer so don't show the success alert
    if (this.jobs.length === response.length) {
      alert.dismiss()
      return
    }

    alert
      .withState('success')
      .saying(
        'Success!',
        `${this.jobs.length - response.length} jobs were transferred to ${target}`,
      )

    this.modal.close()
  }

  /**
   * Transfers job to the pool.
   */
  private async _transferJobToPool() {
    const jobsCount = this.jobs.length

    const alert = this._newAlert()
      .withState('pending')
      .saying(
        'Transferring to pool',
        `Transferring ${jobsCount === 1 ? this.jobs[0].name : `${jobsCount} jobs`} to Pool...`,
      )
      .autoDismiss()
      .show()

    if (this.jobs.length === 1) {
      try {
        await this._jobStore.transferJobToPoolAsAdmin({
          jobId: this.jobs[0].id,
          reason: this.reason,
        })
      } catch (err) {
        alert.dismiss()
        throw err
      }

      alert
        .withState('success')
        .saying('Success!', `${this.jobs[0].name} was transferred to Pool...`)

      return
    }

    const response = await this._jobStore
      .bulkTransferJobsToPool({
        jobIds: this.jobs.map((j) => j.id),
        reason: this.reason,
      })
      .catch((err: unknown) => {
        alert.dismiss()
        throw err
      })

    this._handleBulkResponse(alert, response, 'Pool')
  }

  /**
   * Transfer to client.
   */
  private async _transferJobToClient() {
    const jobsCount = this.jobs.length

    const client = this.transferViewModels.Client.client

    const alert = this._newAlert()
      .withState('pending')
      .saying(
        'Transferring to new client',
        `Transferring ${
          client?.displayName
        } to ${jobsCount === 1 ? this.jobs[0].name : `${jobsCount} jobs`}...`,
      )
      .autoDismiss()
      .show()

    if (this.jobs.length === 1) {
      try {
        await this._jobStore.transferJobToClientAsAdmin({
          jobId: this.jobs[0].id,
          clientUserPublicId: client?.user.publicId,
          reason: this.reason,
        })
      } catch (err) {
        alert.dismiss()
        throw err
      }

      alert
        .withState('success')
        .saying(
          'Success!',
          `${client?.displayName} was transferred to ${this.jobs[0].name}.`,
        )
        .autoDismiss()

      return
    }

    const response = await this._jobStore
      .bulkTransferJobsToClientAsAdmin({
        jobIds: this.jobs.map((j) => j.id),
        clientId: this.transferViewModels.Client.client?.user.publicId,
        reason: this.reason,
      })
      .catch((err: unknown) => {
        alert.dismiss()
        throw err
      })

    this._handleBulkResponse(alert, response, client?.displayName)
  }

  /**
   * Transfer to pro.
   */
  private async _transferJobToPro() {
    const proVm = this.transferViewModels.Pro

    const jobsCount = this.jobs.length

    const alert = this._newAlert()
      .withState('pending')
      .saying(
        'Transferring to new pro',
        `Transferring ${proVm.pro?.displayName} to ${this.jobs[0].name}...`,
      )
      .autoDismiss()
      .show()

    if (jobsCount === 1) {
      try {
        await this._jobStore.transferJobToProAsAdmin({
          jobId: this.jobs[0].id,
          providerUserPublicId: proVm.pro?.user.publicId,
          reason: this.reason,
        })
      } catch (err) {
        alert.dismiss()
        throw err
      }
      alert
        .withState('success')
        .saying(
          'Success!',
          `${proVm.pro?.displayName} was transferred to ${this.jobs[0].name}.`,
        )
        .autoDismiss()

      return
    }

    const response = await this._jobStore
      .bulkTransferJobsToProviderAsAdmin({
        jobIds: this.jobs.map((job) => job.id),
        providerId: proVm.pro?.user.publicId,
        reason: this.reason,
      })
      .catch((err: unknown) => {
        alert.dismiss()
        throw err
      })

    this._handleBulkResponse(alert, response, proVm.pro?.displayName)
  }

  /**
   * Transfer to client team.
   */
  private async _transferJobToClientTeam() {
    const jobsCount = this.jobs.length

    const clientTeamName =
      this.transferViewModels.Team.clientTeamMember?.team.name

    const alert = this._newAlert()
      .withState('pending')
      .saying(
        'Transferring to new client team',
        `Transferring ${jobsCount === 1 ? this.jobs[0].name : `${jobsCount} jobs`} to ${
          clientTeamName
        }...`,
      )
      .autoDismiss()
      .show()

    if (this.jobs.length === 1) {
      try {
        await this._jobStore.transferJobToClientTeamAsAdmin({
          jobId: this.jobs[0].id,
          teamId: this.transferViewModels.Team.clientTeamMember?.team.id,
          reason: this.reason,
        })
      } catch (err) {
        alert.dismiss()
        throw err
      }

      alert
        .withState('success')
        .saying(
          'Success!',
          `${this.jobs[0].name} was transferred to ${clientTeamName}....`,
        )

      return
    }

    const response = await this._jobStore
      .bulkTransferJobToClientTeamAsAdmin({
        jobIds: this.jobs.map((job) => job.id),
        teamId: this.transferViewModels.Team.clientTeamMember?.team.id,
        reason: this.reason,
      })
      .catch((err: unknown) => {
        alert.dismiss()
        throw err
      })

    this._handleBulkResponse(alert, response, clientTeamName)
  }

  /**
   * Whether the current user may transfer the job as an admin, which is also
   * what transferring it to a client takes.
   */
  private _canTransferJobAsAdmin(job: Job) {
    if (job.completed) {
      return false
    }

    const { permissions } = this._getCurrentMember(job.workspace.id)
    return (
      permissions.adminJobTransfer &&
      permissions.adminUserList &&
      permissions.adminUserRead &&
      permissions.adminJobView
    )
  }

  /**
   * A job with an active consultation can only be transferred while it is
   * claimed or on hold.
   */
  private _canTransferConsultationJobAsAdmin(job: Job) {
    return job.hasActiveConsultation
      ? (job.isClaimedJob || job.isOnHold) && this._canTransferJobAsAdmin(job)
      : this._canTransferJobAsAdmin(job)
  }

  private _canTransferToPoolAsAdmin(job: Job) {
    return (
      !job.completed &&
      !job.isPending &&
      this._getCurrentMember(job.workspace.id).permissions.adminJobTransfer
    )
  }

  private _canTransferToProAsAdmin(job: Job) {
    return (
      this._canTransferJobAsAdmin(job) &&
      this._getCurrentMember(job.workspace.id).permissions
        .adminJobChampionProviderManage
    )
  }

  private _canTransferToClientTeamAsAdmin(job: Job) {
    return (
      this._canTransferJobAsAdmin(job) &&
      this._getCurrentMember(job.workspace.id).permissions.adminAssignTeam
    )
  }
}
